import { Skeleton } from './ui/skeleton';

interface OrderDetailsSkeletonProps {
  isMobile?: boolean;
}

export function OrderDetailsSkeleton({ isMobile = false }: OrderDetailsSkeletonProps) {
  const modalMaxWidth = isMobile ? '100%' : '700px';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: isMobile ? 0 : 'auto',
        bottom: 0,
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translateX(-50%)',
        backgroundColor: '#ffffff',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: modalMaxWidth,
        width: isMobile ? '100%' : modalMaxWidth,
        marginLeft: isMobile ? 0 : 'auto',
        marginRight: isMobile ? 0 : 'auto',
        boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Header Skeleton */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingTop: '12px',
            paddingBottom: '12px',
          }}
        >
          <Skeleton
            delay={0}
            style={{
              height: '20px',
              width: '80px',
              borderRadius: '4px',
            }}
          />
        </div>
      </header>

      {/* Content Skeleton */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {/* Hero Section */}
        <div
          style={{
            paddingTop: '20px',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '0',
          }}
        >
          {/* Title */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '16px',
              marginBottom: '4px',
            }}
          >
            <Skeleton
              delay={50}
              style={{
                height: '24px',
                width: '180px',
                borderRadius: '4px',
              }}
            />
          </div>

          {/* Category */}
          <div style={{ marginBottom: '8px' }}>
            <Skeleton
              delay={100}
              style={{
                height: '20px',
                width: '140px',
                borderRadius: '4px',
              }}
            />
          </div>

          {/* Badges */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            <Skeleton
              delay={150}
              style={{
                height: '24px',
                width: '80px',
                borderRadius: '6px',
              }}
            />
            <Skeleton
              delay={200}
              style={{
                height: '24px',
                width: '70px',
                borderRadius: '6px',
              }}
            />
          </div>
        </div>

        {/* Description Skeleton */}
        <div style={{ padding: '24px' }}>
          <Skeleton
            delay={250}
            style={{
              height: '16px',
              width: '100%',
              borderRadius: '4px',
              marginBottom: '8px',
            }}
          />
          <Skeleton
            delay={300}
            style={{
              height: '16px',
              width: '100%',
              borderRadius: '4px',
              marginBottom: '8px',
            }}
          />
          <Skeleton
            delay={350}
            style={{
              height: '16px',
              width: '85%',
              borderRadius: '4px',
            }}
          />
        </div>

        {/* Gallery Skeleton */}
        <div
          style={{
            backgroundColor: 'white',
            position: 'relative',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)',
              paddingLeft: '24px',
              paddingTop: '16px',
              paddingBottom: '12px',
            }}
          >
            <Skeleton
              delay={400}
              style={{
                height: '20px',
                width: '60px',
                borderRadius: '4px',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              paddingLeft: '24px',
            }}
          >
            {[1, 2, 3].map((index) => (
              <Skeleton
                key={index}
                delay={450 + (index * 50)}
                style={{
                  flexShrink: 0,
                  width: '128px',
                  height: '128px',
                  borderRadius: '8px',
                }}
              />
            ))}
          </div>

          <div style={{ height: '24px' }} />
        </div>

        {/* Location & Client Info Skeleton */}
        <div style={{ padding: '24px' }}>
          {/* Location Card */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0px 20px 50px 10px rgba(10, 33, 94, 0.12)',
              border: '1px solid #E5E7EB',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <Skeleton
                delay={600}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  delay={650}
                  style={{
                    height: '20px',
                    width: '150px',
                    borderRadius: '4px',
                    marginBottom: '4px',
                  }}
                />
                <Skeleton
                  delay={700}
                  style={{
                    height: '16px',
                    width: '120px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}
                />
                <Skeleton
                  delay={750}
                  style={{
                    height: '16px',
                    width: '100px',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Client Information Card */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0px 20px 50px 10px rgba(10, 33, 94, 0.12)',
              border: '1px solid #E5E7EB',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '24px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <Skeleton
                  delay={800}
                  style={{
                    height: '20px',
                    width: '140px',
                    borderRadius: '4px',
                    marginBottom: '24px',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                }}
              >
                {[1, 2].map((index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Skeleton
                      delay={850 + (index * 50)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '18px',
                      }}
                    />
                    <Skeleton
                      delay={900 + (index * 50)}
                      style={{
                        height: '14px',
                        width: '120px',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Request Details Skeleton */}
        <div
          style={{
            padding: '24px',
            paddingTop: '32px',
          }}
        >
          <Skeleton
            delay={1000}
            style={{
              height: '20px',
              width: '140px',
              borderRadius: '4px',
              marginBottom: '24px',
            }}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {[1, 2, 3].map((index) => {
              const baseDelay = 1050 + (index * 100);
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#f6f8fa',
                    borderRadius: '8px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    <Skeleton
                      delay={baseDelay}
                      style={{
                        width: '10px',
                        height: '16px',
                        borderRadius: '4px',
                        flexShrink: 0,
                      }}
                    />
                    <Skeleton
                      delay={baseDelay + 30}
                      style={{
                        height: '16px',
                        width: `${200 + (index * 30)}px`,
                        borderRadius: '4px',
                        flex: 1,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      width: '100%',
                      borderLeft: '1px solid #E1E6E9',
                      padding: '12px 16px',
                    }}
                  >
                    <Skeleton
                      delay={baseDelay + 60}
                      style={{
                        height: '16px',
                        width: '100%',
                        borderRadius: '4px',
                        marginBottom: '4px',
                      }}
                    />
                    <Skeleton
                      delay={baseDelay + 90}
                      style={{
                        height: '16px',
                        width: '85%',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ height: '16px' }} />
      </div>

      {/* Footer CTA Skeleton */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingBottom: '16px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Skeleton
          delay={1400}
          style={{
            height: '48px',
            width: '100%',
            borderRadius: '12px',
          }}
        />
      </div>
    </div>
  );
}
